import { useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { useSelectedIds } from "@ryde/redux";
import {
  IPassengerStationsMapInfo,
  PassengerMapControlsEnum,
  passengerMapIsPickupStation,
  passengersMapSelectedControlBtnAtom,
  passengersMapSelectedStationDictionaryAtom,
} from "@ryde/shared";

export const usePassengerMapSelectedStations = () => {
  const { selectedRowsIds } = useSelectedIds("passenger");
  const [
    passengersMapSelectedStationDictionary,
    setPassengersMapSelectedStationDictionary,
  ] = useRecoilState(passengersMapSelectedStationDictionaryAtom);

  const passengersMapSelectedControlBtn = useRecoilValue(
    passengersMapSelectedControlBtnAtom,
  );

  const isLinkStationControlOpen =
    passengersMapSelectedControlBtn === PassengerMapControlsEnum.linkStations;

  const passengersIdsAssignSelectedStations = useMemo(() => {
    const selectedTablePassengersIds = new Set(selectedRowsIds);

    const assignHomePassengers = new Set<number>();
    const assignPickupPassengers = new Set<number>();

    for (const stationMapData of passengersMapSelectedStationDictionary.values()) {
      const { assignPassengers, stationInfo } = stationMapData ?? {};

      const targetSet = passengerMapIsPickupStation(stationInfo)
        ? assignPickupPassengers
        : assignHomePassengers;

      for (const { id } of assignPassengers) {
        if (selectedTablePassengersIds.has(id)) {
          targetSet.add(id);
        }
      }
    }

    return {
      assignHomePassengers,
      assignPickupPassengers,
    };
  }, [passengersMapSelectedStationDictionary, selectedRowsIds]);

  const handleStationClick = useEvent(
    (
      stationMapData: IPassengerStationsMapInfo,
      isToggleBehavior = true,
    ): void => {
      const stationId = stationMapData?.stationInfo?.id;
      if (!stationId) {
        return;
      }

      setPassengersMapSelectedStationDictionary(prev => {
        const next = new Map(prev);

        if (isToggleBehavior && next.delete(stationId)) {
          return next;
        }

        if (
          passengerMapIsPickupStation(stationMapData.stationInfo) &&
          isLinkStationControlOpen
        ) {
          for (const [id, s] of next) {
            if (passengerMapIsPickupStation(s.stationInfo)) {
              next.delete(id);
            }
          }
        }

        next.set(stationId, stationMapData);
        return next;
      });
    },
  );

  const handleBulkAddSelectedStation = useEvent(
    (stationsMapData: IPassengerStationsMapInfo[]): void => {
      if (!stationsMapData.length) {
        return;
      }

      setPassengersMapSelectedStationDictionary(prev => {
        const next = new Map(prev);

        for (const stationMapData of stationsMapData) {
          next.set(stationMapData.stationInfo.id, stationMapData);
        }

        return next;
      });
    },
  );

  const resetSelectedStation = useEvent(
    ({
      removePickup = false,
      removeHomeAddress = false,
    }: {
      removePickup?: boolean;
      removeHomeAddress?: boolean;
    } = {}): void => {
      setPassengersMapSelectedStationDictionary(prev => {
        if (!removePickup && !removeHomeAddress) {
          return new Map();
        }

        const next = new Map(prev);

        for (const [id, s] of prev) {
          const isPickup = passengerMapIsPickupStation(s.stationInfo);
          const shouldRemove =
            (removePickup && isPickup) || (removeHomeAddress && !isPickup);

          if (shouldRemove) {
            next.delete(id);
          }
        }

        return next;
      });
    },
  );

  return {
    handleStationClick,
    resetSelectedStation,
    handleBulkAddSelectedStation,
    passengersIdsAssignSelectedStations,
    passengersMapSelectedStationDictionary,
  };
};
